import type { DraftCheck } from "./admin";
import { apiRequest } from "./client";
import type { CrossFilterValue } from "../runtime/CrossFilterContext";
import type { AiChatRequest, AiChatResponse, Dataset, DraftSnapshot, DraftSnapshotDetail, ReportVersionDetail, VersionDiff, PresenceBeat, PresenceInfo, Report, ReportDraft, ReportVersion } from "../types";
import type { SharePointDrive, SharePointDriveItem } from "./dataSources";

export const ownerApi = {
  listReports: () => apiRequest<Report[]>("/api/v1/owner/reports"),
  /** v2.2: owners start their own draft reports (creator gets edit, not publish). */
  createReport: (payload: { name: string; description?: string }) =>
    apiRequest<Report>("/api/v1/owner/reports", { method: "POST", body: payload }),
  getReport: (id: string) => apiRequest<Report>(`/api/v1/owner/reports/${id}`),
  getDraft: (id: string) => apiRequest<ReportDraft>(`/api/v1/owner/reports/${id}/draft`),
  getPreviewData: (id: string, filters?: CrossFilterValue[] | null) => {
    const params = filters && filters.length > 0 ? `?filters=${encodeURIComponent(JSON.stringify(filters))}` : "";
    return apiRequest<Record<string, unknown>>(`/api/v1/owner/reports/${id}/preview-data${params}`);
  },
  saveDraft: (id: string, payload: { definition: Record<string, unknown>; expectedRevision: number }) =>
    apiRequest<ReportDraft>(`/api/v1/owner/reports/${id}/draft`, { method: "PUT", body: payload }),
  publishReport: (id: string) =>
    apiRequest<ReportVersion>(`/api/v1/owner/reports/${id}/publish`, { method: "POST" }),
  listVersions: (id: string) => apiRequest<ReportVersion[]>(`/api/v1/owner/reports/${id}/versions`),
  listDraftSnapshots: (id: string) => apiRequest<DraftSnapshot[]>(`/api/v1/owner/reports/${id}/draft-snapshots`),
  getVersionDetail: (id: string, versionId: string) => apiRequest<ReportVersionDetail>(`/api/v1/owner/reports/${id}/versions/${versionId}`),
  getVersionDiff: (id: string, versionId: string, against = "previous") => apiRequest<VersionDiff>(`/api/v1/owner/reports/${id}/versions/${versionId}/diff?against=${encodeURIComponent(against)}`),
  getDraftSnapshotDetail: (id: string, snapshotId: string) => apiRequest<DraftSnapshotDetail>(`/api/v1/owner/reports/${id}/draft-snapshots/${snapshotId}`),
  getDraftSnapshotDiff: (id: string, snapshotId: string, against = "previous") => apiRequest<VersionDiff>(`/api/v1/owner/reports/${id}/draft-snapshots/${snapshotId}/diff?against=${encodeURIComponent(against)}`),
  /** Designer "Ask AI": takes 20-60 s, so no timeout; pass a signal to cancel. Never saves the draft. */
  aiChat: (id: string, payload: AiChatRequest, signal?: AbortSignal) =>
    apiRequest<AiChatResponse>(`/api/v1/owner/reports/${id}/ai/chat`, { method: "POST", body: payload, signal }),
  presence: (id: string, beat: PresenceBeat) =>
    apiRequest<PresenceInfo>(`/api/v1/owner/reports/${id}/presence`, { method: "POST", body: beat }),
  leavePresence: (id: string, clientId: string) =>
    apiRequest<void>(`/api/v1/owner/reports/${id}/presence?client_id=${encodeURIComponent(clientId)}`, { method: "DELETE" }),
  saveDraftSnapshot: (id: string, label?: string) =>
    apiRequest<DraftSnapshot>(`/api/v1/owner/reports/${id}/draft-snapshots`, { method: "POST", body: { label: label || null } }),
  restoreDraftSnapshot: (id: string, snapshotId: string) =>
    apiRequest<ReportDraft>(`/api/v1/owner/reports/${id}/draft-snapshots/${snapshotId}/restore`, { method: "POST" }),
  validateDraft: (id: string) => apiRequest<DraftCheck>(`/api/v1/owner/reports/${id}/validate`, { method: "POST" }),
};

export interface OwnedSharePointSite {
  tenant_id: string;
  site_id: string;
  site_name: string;
}

export const ownerDataSourceApi = {
  listSites: () => apiRequest<OwnedSharePointSite[]>("/api/v1/owner/data-sources/sites"),
  listDrives: (siteId: string) =>
    apiRequest<SharePointDrive[]>(`/api/v1/owner/data-sources/sharepoint/sites/${encodeURIComponent(siteId)}/drives`),
  browseDrive: (driveId: string, siteId: string, path: string) =>
    apiRequest<SharePointDriveItem[]>(
      `/api/v1/owner/data-sources/sharepoint/drives/${encodeURIComponent(driveId)}/items?site_id=${encodeURIComponent(siteId)}&path=${encodeURIComponent(path)}`,
    ),
  createFromExcel: (payload: {
    name: string;
    site_id: string;
    site_name: string;
    drive_id: string;
    item_id: string;
    sheet_name?: string;
    description?: string;
  }) =>
    apiRequest<{ data_source: unknown; dataset: Dataset }>("/api/v1/owner/data-sources/sharepoint-excel", {
      method: "POST",
      body: payload,
    }),
};
